import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import IconButton from '@material-ui/core/IconButton';

import {
    normalizeProductVideoUrlForStorage,
    resolveProductVideoPreview,
} from '../../../utils/product-video';

const PREVIEW_WIDTH = 340;
const PREVIEW_HEIGHT = 191;

const PORTRAIT_PREVIEW_SIZES = {
    instagram: { width: 220, height: 390 },
    tiktok: { width: 236, height: 418 },
};

const previewFrameStyle = {
    marginTop: 10,
    position: 'relative',
    width: PREVIEW_WIDTH,
    height: PREVIEW_HEIGHT,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#111',
};

const ProductVideoPreview = ({ videoUrl, productData }) => {
    const { t } = useTranslation();
    const [isPlaying, setIsPlaying] = useState(false);
    const [thumbnailFailed, setThumbnailFailed] = useState(false);

    const trimmedUrl = String(videoUrl || '').trim();
    const savedUrl = String(productData?.video_url || '').trim();
    const normalizedCurrent = normalizeProductVideoUrlForStorage(trimmedUrl) || trimmedUrl;
    const normalizedSaved = normalizeProductVideoUrlForStorage(savedUrl) || savedUrl;
    const useSavedMeta = Boolean(normalizedSaved && normalizedCurrent === normalizedSaved);

    const preview = useMemo(
        () =>
            resolveProductVideoPreview({
                videoUrl: trimmedUrl,
                videoProvider: useSavedMeta ? productData?.video_provider : null,
                videoId: useSavedMeta ? productData?.video_id : null,
            }),
        [trimmedUrl, useSavedMeta, productData?.video_provider, productData?.video_id]
    );

    useEffect(() => {
        setIsPlaying(false);
        setThumbnailFailed(false);
    }, [trimmedUrl]);

    if (!preview.embedSrc) {
        return null;
    }

    const isGoogleDrive = preview.provider === 'google_drive' || preview.provider === 'drive';
    const isPortraitEmbed = preview.provider === 'instagram' || preview.provider === 'tiktok';
    const portraitSize = PORTRAIT_PREVIEW_SIZES[preview.provider] || PORTRAIT_PREVIEW_SIZES.instagram;
    const frameStyle = isPortraitEmbed
        ? {
              ...previewFrameStyle,
              width: portraitSize.width,
              height: portraitSize.height,
          }
        : previewFrameStyle;
    const showThumbnail = preview.thumbnailSrc && !thumbnailFailed && !isPlaying;
    const showInlinePlayer =
        isPlaying ||
        isPortraitEmbed ||
        (!isGoogleDrive && (!preview.thumbnailSrc || thumbnailFailed));

    const handlePlay = () => {
        if (isGoogleDrive) {
            window.open(preview.embedSrc, '_blank', 'noopener,noreferrer');
            return;
        }

        setIsPlaying(true);
    };

    return (
        <div style={frameStyle}>
            {showInlinePlayer ? (
                <iframe
                    title={t('Product.videoUrlPreview')}
                    src={preview.embedSrc}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0,
                    }}
                />
            ) : (
                <>
                    {showThumbnail ? (
                        <img
                            src={preview.thumbnailSrc}
                            alt=""
                            onError={() => setThumbnailFailed(true)}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#1a1a1a',
                            }}
                        />
                    )}
                    <IconButton
                        aria-label={t('Product.videoUrlPlayPreview')}
                        onClick={handlePlay}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                            color: '#fff',
                            padding: 8,
                        }}
                    >
                        <PlayArrowIcon />
                    </IconButton>
                </>
            )}
        </div>
    );
};

export default ProductVideoPreview;
